import React from "react"
import { EEntrySearchLayout, EntrySearch } from "@organic-return/foundation-react/src/components/Search/EntrySearch"
import { useSiteContext } from "../common/SiteContext"
import { graphql, Link, navigate, useStaticQuery } from "gatsby"
import { BackgroundImage } from "@organic-return/foundation-react/src/components/Media"
import { EWsrvTransform, IListing } from "@organic-return/foundation-react/src/lib"
import { Carousel } from "@organic-return/foundation-react/src/components/Carousel"
import ListingCard, { EListingCardLayouts } from "@organic-return/foundation-react/src/components/Listing/ListingCard"
import { EListingVitalsLayout } from "@organic-return/foundation-react/src/components/Listing/ListingVitals"

export const RentalEntrySearch = ({ module }) => {
  const data = useStaticQuery(graphql`
    query RentalSearchEntryQuery {
      config: agilitySiteConfiguration(properties:{referenceName:{ne:"siteconfiguration_deleted"}}) {
        customFields {
          rentalSearchPath
        }
      }
      rentalEntrySearch: allAgilityRentalEntrySearch {
        nodes {
          contentID
          listings {
            id            
            beds
            baths
            bathsHalf
            sleeps
            lotacres
            majorArea
            exclusive
            location {
              lat
              lng
            }
            minorArea
            mlsID
            price
            priceLow
            priceHigh
            priceSuffix
            sqft
            status
            defaultImage {
              dimensions
              url
            }            
            flags
            features           
            address {
              text
              street1
              street2
              state
              postal
              county
              country
              city
            }
            path
            exclusive
          }          
        }
      }
    }
  `)
  const siteContext = useSiteContext()
  const fields = module.customFields
  let entrySearchProps = {
    ...siteContext.rentalEntrySearchProps
  }
  if (fields.displayAdvancedSearch) {
    entrySearchProps = {
      ...siteContext.rentalEntryAdvancedSearchProps,
      layout: EEntrySearchLayout.col5row2,
    }
  }
  if (fields.displayRentalCarousel) {
    entrySearchProps = {
      ...entrySearchProps,
      className: "text-white inverted md:bg-transparent xl:absolute left-0 right-[25%] bottom-0 lg:mx-4",
    }
  }
  let component = (
    <EntrySearch
      title={fields.title}
      onSubmit={async filters => {
        const params = new URLSearchParams({
          filters: JSON.stringify(filters),
        })
        await navigate(
          data.config.customFields.rentalSearchPath + "#" + params.toString()
        )
      }}
      {...entrySearchProps}
    />
  )
  if (!fields.displayRentalCarousel) {
    component = (
      <div className="w-full max-w-screen-lg">
        {component}
      </div>
    )
  }
  let itemsClass = "items-center"
  fields.searchPosition == "bottom" && (itemsClass = "items-end")
  fields.searchPosition == "top" && (itemsClass = "items-start")

  let node = data.rentalEntrySearch.nodes.find(
    node => node.contentID == module.contentID
  )
  let rentalListings = node.listings ?? []

  return (
    <div className="rental-entry-search">
      {!fields.displayRentalCarousel && fields.backgroundImage?.url && (
        <BackgroundImage
          src={fields.backgroundImage?.url}
          className={`h-screen max-h-[1000px] text-white flex ${itemsClass} justify-center py-24`}
          sizesAndAspect={[
            {
              width: 420,
              aspect: EWsrvTransform.cover2x6,
            },
            {
              media: "min-width:420px",
              width: 640,
              aspect: EWsrvTransform.cover2x6,
            },
            {
              media: "min-width:640px",
              width: 768,
              aspect: EWsrvTransform.cover3x2,
            },
            {
              media: "min-width:768px",
              width: 1024,
              aspect: EWsrvTransform.cover3x2,
            },
            {
              media: "min-width:1024px",
              width: 1280,
              aspect: EWsrvTransform.cover16x9,
            },
            {
              media: "min-width:1280px",
              width: 1440,
              aspect: EWsrvTransform.cover16x9,
            },
          ]}
        >
          {component}
        </BackgroundImage>
      )}
      {fields.displayRentalCarousel && rentalListings.length > 0 && (
        <Carousel
          staticOverlayChildren={component}
          className="inverted"
        >
          {rentalListings.map((listing: IListing, index: number) => (
            <ListingCard
              layout={EListingCardLayouts.composite}
              vitalsLayout={EListingVitalsLayout.column1Border0}
              vitalsClassName="text-center md:pl-[75%]"
              vitalsIncludeVitalsIcons={false}
              listing={listing}
              className=""
              maxWidthImage={2400}
              key={index}
              imageProps={{
                imgProps: {
                  className:
                    "w-full h-auto xl:h-full xl:object-cover xl:object-center",
                  wrapperClassName: "w-full xl:h-screen xl:max-h-screen",
                  wrapperProps: {
                    style: {
                      display: "block"
                    }
                  }
                }
              }}
              linkFunc={props => {
                return (
                  <Link
                    to={props.href ?? ""}
                    className={`hide-disabled text-inherit ${props.className}`}
                    key={props.key}
                    title={props.title}
                  >
                    <>{props.children}</>
                  </Link>
                )
              }}
              {...siteContext.globalListingCardProps}
            />
          ))}
        </Carousel>
      )}
      {((!fields.displayRentalCarousel || rentalListings.length == 0) && !fields.backgroundImage?.url) && component}
    </div>
  )
}

export default RentalEntrySearch
